<template>
  <form
    class="bundle-product-form uk-form-stacked"
    @submit.prevent="submit"
  >
    <!-- Basic Data -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend">{{$t('fieldsets.basicFields')}}</legend>

      <product-family-combobox
        v-model="fields.values.family"
        :label="$t('fields.family')"
        :errors="fields.errors.family"
        :disabled="!isEditable"
        :filter="{ bundle: true }"
      />

      <text-input
        type="text"
        ref="nameInput"
        :label="$t('fields.name')"
        v-model="fields.values.name"
        :errors="fields.errors.name"
        :disabled="!isEditable"
      />

      <text-input
        type="text"
        :label="$t('fields.articleNumber')"
        v-model="fields.values.articleNumber"
        :errors="fields.errors.articleNumber"
        :disabled="!isEditable"
      />

      <keyword-list-input
        :label="$t('fields.tags')"
        v-model="fields.values.tags"
        :errors="fields.errors.tags"
        :disabled="!isEditable"
      />
    </fieldset>

    <!-- Bundled Products -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">
        {{$t('fieldsets.bundledProducts')}}
      </legend>

      <div v-if="fields.values.family" class="uk-margin">
        <loader v-if="bundleDefinitionsLoading">
          {{$t('loaders.bundledProducts')}}
        </loader>
        <div v-else>
          <template v-if="bundleDefinitions.length">
            <div
              v-if="fields.values.bundlings[index]"
              v-for="(definition, index) in bundleDefinitions"
            >
              <product-combobox
                v-model="fields.values.bundlings[index].product"
                :label="definition.productFamily.name"
                :errors="fields.errors.bundlings[index] && fields.errors.bundlings[index].product"
                :disabled="!isEditable"
                :filter="{
                  bundle: false,
                  familyId: definition.productFamily.id
                }"
              />
            </div>
          </template>

          <alert-box v-else type="warning" icon="warning">
            {{$t('hints.missingBundleDefinitions')}}
            <br />
            <router-link
              :to="{ name: 'product-family', params: { id: fields.values.family.id } }">
              {{$t('actions.solveIssue')}}
            </router-link>
          </alert-box>
        </div>
      </div>
      <alert-box v-else icon="info">
        {{$t('prompts.selectFamily')}}
      </alert-box>
    </fieldset>

    <!-- Details -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend">
        {{$t('fieldsets.details')}}
      </legend>

      <text-input
        type="text"
        :label="$t('fields.altName')"
        v-model="fields.values.altName"
        :errors="fields.errors.altName"
        :disabled="!isEditable"
      />

      <text-list-input
        :label="$t('fields.shortUsp')"
        v-model="fields.values.shortUsp"
        :errors="fields.errors.shortUsp"
        :disabled="!isEditable"
      />

      <text-list-input
        :label="$t('fields.usps')"
        v-model="fields.values.usps"
        :errors="fields.errors.usps"
        :disabled="!isEditable"
      />

      <text-list-input
        :label="$t('fields.summaryTexts')"
        v-model="fields.values.summaryTexts"
        :errors="fields.errors.summaryTexts"
        :disabled="!isEditable"
      />
    </fieldset>

    <!-- SEO -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend">
        {{$t('fieldsets.seo')}}
      </legend>

      <text-input
        type="text"
        :label="$t('fields.slug')"
        v-model="fields.values.slug"
        :errors="fields.errors.slug"
        :disabled="!isEditable"
        :placeholder="slugPlaceholder"
      />

      <textarea-input
        :label="$t('fields.description')"
        v-model="fields.values.description"
        :errors="fields.errors.description"
        :disabled="!isEditable"
        rows="5"
      />

      <keyword-list-input
        :label="$t('fields.aliases')"
        v-model="fields.values.aliases"
        :errors="fields.errors.aliases"
        :disabled="!isEditable"
      />

      <keyword-list-input
        :label="$t('fields.keywords')"
        v-model="fields.values.keywords"
        :errors="fields.errors.keywords"
        :disabled="!isEditable"
      />
    </fieldset>

    <!-- Legal Texts -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">
        {{$t('fieldsets.legalTexts')}}
      </legend>

      <legal-text-manager
        v-model="fields.values.legalTexts"
        :errors="fields.errors.legalTexts"
        :disabled="!isEditable"
      />
    </fieldset>

    <!-- Media -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">
        {{$t('fieldsets.media')}}
      </legend>

      <attachment-manager
        attachment-type="MEDIA_OBJECT"
        v-model="fields.values.media"
        :errors="fields.errors.media"
        :disabled="!isEditable"
      />
    </fieldset>

     <Checkbox v-model="fields.values.alwaysDisplayed" :label="'Always Displayed?'"/>

    <div class="uk-margin-medium-top">
      <submit-button type="primary" :disabled="!isSubmittable" />
      <cancel-button />
    </div>
  </form>
</template>

<script>
import every from 'lodash/every';
import AttachmentManager from '@/components/AttachmentManager';
import CancelButton from '@/components/CancelButton';
import CommonButton from '@/components/CommonButton';
import Form from '@/mixins/Form';
import LegalTextManager from '@/components/LegalTextManager';
import Loader from '@/components/Loader';
import KeywordListInput from '@/components/KeywordListInput';
import ProductCombobox from '@/components/ProductCombobox';
import ProductFamilyCombobox from '@/components/ProductFamilyCombobox';
import productFamilyQuery from '@/api/queries/product-family.graphql';
import Progress from '@/mixins/Progress';
import slugQuery from '@/api/queries/slug.graphql';
import SubmitButton from '@/components/SubmitButton';
import TextareaInput from '@/components/TextareaInput';
import TextInput from '@/components/TextInput';
import TextListInput from '@/components/TextListInput';
import ValidationAlertBox from '@/components/ValidationAlertBox';
import Checkbox from '@/components/Checkbox';


export default {
  name: 'bundle-product-form',
  mixins: [
    Form({
      aliases: { type: Form.Types.Array },
      altName: true,
      articleNumber: { required: true },
      bundlings: {
        type: Form.Types.Array,
        required: true,
        isPresent(bundlings) {
          if (!bundlings) return false;
          return every(bundlings, bundling => bundling && bundling.product);
        },
        dump(bundlings) {
          if (!bundlings) return [];
          return bundlings.map((bundling) => {
            const data = { definitionId: null, productId: null };
            if (bundling.definition) {
              data.definitionId = bundling.definition.id;
            }
            if (bundling.product) data.productId = bundling.product.id;
            return data;
          });
        },
      },
      description: true,
      family: { type: Form.Types.Reference, required: true },
      name: { required: true },
      legalTexts: { type: Form.Types.ReferenceList },
      keywords: { type: Form.Types.Array },
      media: { type: Form.Types.Array, dumpTo: 'mediaObjects' },
      slug: true,
      summaryTexts: { type: Form.Types.Array },
      tags: { type: Form.Types.Array },
      usps: { type: Form.Types.Array },
      shortUsp: { type: Form.Types.Array },
      alwaysDisplayed: true,
    }),
    Progress,
  ],
  components: {
    AttachmentManager,
    CancelButton,
    CommonButton,
    KeywordListInput,
    LegalTextManager,
    Loader,
    ProductCombobox,
    ProductFamilyCombobox,
    SubmitButton,
    TextareaInput,
    TextInput,
    TextListInput,
    ValidationAlertBox,
    Checkbox,
  },
  props: {
    productFamily: Object,
  },
  data() {
    return {
      bundleDefinitionsLoading: 0,
    };
  },
  mounted() {
    this.$refs.nameInput.focus();
    if (!this.fields.values.family) {
      this.fields.values.family = this.productFamily;
    }
  },
  watch: {
    productFamily(family) {
      if (!this.fields.values.family) {
        this.fields.values.family = family;
      }
      this.fields.values.bundlings = [];
    },
  },
  apollo: {
    slugPlaceholder: {
      query: slugQuery,
      variables() {
        return { value: this.fields.values.name };
      },
      fetchPolicy: 'network-only',
      debounce: { wait: 500 },
      update({ slug }) {
        return slug;
      },
      skip() {
        return !this.fields.values.name;
      },
    },
    bundleDefinitions: {
      query: productFamilyQuery,
      variables() {
        return { id: this.fields.values.family.id };
      },
      fetchPolicy: 'network-only',
      loadingKey: 'bundleDefinitionsLoading',
      update({ productFamily }) {
        return productFamily.bundlings;
      },
      result({ data }) {
        this.prepareBundlings(data.productFamily.bundlings || []);
      },
      skip() {
        return !this.fields.values.family;
      },
    },
  },
  methods: {
    prepareBundlings(definitions) {
      const bundlings = [];
      const errors = [];
      definitions.forEach((definition, index) => {
        const originalBundlings = this.fields.values.bundlings || [];
        const bundling = originalBundlings.find(b =>
          b.definition.id === definition.id,
        );
        const product = bundling ? bundling.product : null;
        bundlings[index] = { product, definition };
        errors[index] = { product: [] };
      });
      this.fields.values.bundlings = bundlings;
      this.fields.errors.bundlings = errors;
    },
  },
  i18n: {
    messages: {
      de: {
        actions: {
          solveIssue: 'Diesen Fehler beheben',
        },
        fieldsets: {
          base: 'Basisdaten',
          bundledProducts: 'Enthaltene Produkte',
        },
        loaders: {
          bundledProducts: 'Lade Bundle-Definition…',
        },
        hints: {
          missingBundleDefinitions: `Die Produktfamilie des Bundles definiert
                                     nicht, welche Produktfamilien das Bundle
                                     enthalten darf.`,
        },
        prompts: {
          selectFamily: `Bitte wähle eine Produktfamilie aus, um verfügbare
                         Produktvarianten auswählen zu können.`,
        },
      },
    },
  },
};
</script>



// WEBPACK FOOTER //
// BundleProductForm.vue?0b602c2e